import { useState, useRef, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Calendar, Briefcase, GraduationCap, ExternalLink } from "lucide-react";
import Footer from "../Components/Footer";
import Navbar from "../Components/Navbar";

export default function ExperiencePage() {
  const [activeTab, setActiveTab] = useState("experience");
  useEffect(() => {
    document.title = "Harsh Amin | Experience";
  }, []);

  return (
    <div className="flex flex-col w-full bg-gradient-to-br from-slate-950">
      <Navbar />
      <div className="min-h-screen flex items-center justify-center pt-10">
        <div className="max-w-7xl">
          <motion.h1
            className="text-4xl md:text-5xl font-bold text-center mb-4 text-slate-100"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            My Journey
          </motion.h1>

          <motion.p
            className="text-lg text-center mb-12 text-slate-400 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Explore my professional experience and educational background
          </motion.p>

          <div className="flex justify-center mb-12">
            <div className="inline-flex bg-slate-800 p-1 rounded-xl">
              <button
                onClick={() => {
                  document.title = "Harsh Amin | Experience";
                  setActiveTab("experience");
                }}
                className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  activeTab === "experience"
                    ? "bg-slate-700 text-white shadow-sm"
                    : "text-slate-400 hover:text-white"
                }`}
              >
                <Briefcase className="w-4 h-4 mr-2" />
                Experience
              </button>
              <button
                onClick={() => {
                  document.title = "Harsh Amin | Education";
                  setActiveTab("education");
                }}
                className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  activeTab === "education"
                    ? "bg-slate-700 text-white shadow-sm"
                    : "text-slate-400 hover:text-white"
                }`}
              >
                <GraduationCap className="w-4 h-4 mr-2" />
                Education
              </button>
            </div>
          </div>

          {activeTab === "experience" ? (
            <ExperienceTimeline />
          ) : (
            <EducationTimeline />
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}

function ExperienceTimeline() {
  const experiences = [
    {
      id: 1,
      title: "Machine Learning Engineer Intern",
      company: "Amazon · AI Center of Excellence",
      logo: "/amazon.png",
      period: "2026 May - 2026 Aug",
      description:
        "Built evaluation tooling for production AI agents: a test-generation platform (AWS CDK construct) that turns live traffic into approval-gated regression tests, cutting authoring time by 85%, and a trace-clustering pipeline (BGE-M3 embeddings, density-based clustering, Dynamic Time Warping) that distilled 8,000+ agent runs into ~380 behavioural scenarios. Co-authored SEGRA, a paper on experience-guided graph reasoning agents.",
      skills: [
        "AI Agents",
        "LLM Evaluation",
        "Embeddings",
        "Clustering",
        "AWS CDK",
        "Fargate",
        "CloudFront",
      ],
      link: "https://aws.amazon.com/blogs/machine-learning/establishing-an-ai-ml-center-of-excellence/",
    },
    {
      id: 2,
      title: "GenAI Software Developer Intern",
      company: "UBC Cloud Innovation Centre × AWS",
      logo: "/cic.png",
      period: "2025 May - 2025 Dec",
      description:
        "Shipped 3+ open-source GenAI applications end-to-end for sponsors, including a BCcampus study platform for 100,000+ students with a LangChain RAG pipeline over 750+ textbooks on AWS Bedrock. Built an MCP-based AI agent with DynamoDB conversational memory, moved the team to a standard AWS CI/CD pipeline (40% faster deploys), and codified a reusable CDK security baseline as Claude Code and Kiro agent skills.",
      skills: [
        "AWS Bedrock",
        "RAG",
        "LangChain",
        "MCP",
        "AWS CDK",
        "CI/CD",
        "Claude Code",
        "Kiro",
      ],
      link: "https://cic.ubc.ca/",
    },
    {
      id: 3,
      title: "President",
      company: "UBC Launch Pad",
      logo: "/launchpad.jpeg",
      period: "2026 Jan - Present",
      description:
        "Lead UBC's largest student software engineering team: 50+ members across 5+ product teams building software for UBC labs, non-profits and local companies. Raised $2,000+ in sponsorships and run hackathons and tech mixers for UBC's builder community. Previously Technical Lead on Forum AI, and started as a Software Developer on the team.",
      skills: [
        "Leadership",
        "Project Management",
        "Partnerships",
        "Sponsorships",
        "Event Planning",
      ],
      link: "https://www.ubclaunchpad.com/",
    },
    {
      id: 4,
      title: "Technical Software Team Lead",
      company: "UBC Launch Pad",
      logo: "/launchpad.jpeg",
      period: "2025 Jan - Present",
      description:
        "Co-lead a 15+ member team of developers and designers on the Forum AI project, arranging weekly syncs and managing tickets over 8 months. Migrated to FastAPI endpoints with trigger-based embedding retrieval from a PGVectordatabase, resulting in a 27% reduction in API response time. Led the migration to the Deno runtime, achieving 20%+ improvement in execution speed within Docker environments.",
      skills: [
        "React",
        "TypeScript",
        "FastAPI",
        "Deno",
        "PostgreSQL",
        "Docker",
        "RAG",
      ],
      link: "https://www.ubclaunchpad.com/",
    },
    {
      id: 5,
      title: "Software Developer",
      company: "UBC Launch Pad",
      logo: "/launchpad.jpeg",
      period: "2024 Sep - 2025 Jan",
      description:
        "As a software developer at UBC Launch Pad, I collaborated with a team in an agile environment to develop the Forum AI project. My contributions included designing parts of the Retrieval Augmented Generation model (chunking engine and vectorization), developing FastAPI endpoints, setting up a PostgreSQL database with migration scripts, and implementing Selenium-based automation.",
      skills: [
        "React",
        "TypeScript",
        "TailwindCSS",
        "FastAPI",
        "PostgreSQL",
        "RAG",
      ],
      link: "https://www.ubclaunchpad.com/",
    },
    {
      id: 6,
      title: "Computer Science TA",
      company: "Henry Wise Wood High School",
      logo: "/hww.jpg",
      period: "Jan 2023 - Jun 2023",
      description:
        "As a Computer Science TA, I assessed student submissions by implementing JUnit test cases and explained complex topics like Linked Lists, OOP, and Sorting algorithms through in-class presentations. I also designed assignments on UI development and OOP, enhancing both my communication skills and students' programming abilities.",
      skills: ["Communication", "Leadership", "JUnit", "UI Development"],
    },
    {
      id: 7,
      title: "Math Instructor",
      company: "Mathnasium",
      logo: "/mathnasium.jpeg",
      period: "Sep 2022 - May 2023",
      description:
        "As a math instructor, I taught high school math (grades 9-12) using the Mathnasium Learning Method, improving my communication and leadership skills through student interactions and coordination with fellow instructors.",
      skills: ["Communication", "Leadership", "Teamwork", "Problem Solving"],
      link: "https://www.mathnasium.com/ca/",
    },
  ];

  return (
    <div className="relative w-full">
      {/* Timeline line */}
      <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-0.5 bg-gradient-to-b from-blue-500 to-purple-600" />

      {/* Experience cards */}
      <div className="relative">
        {experiences.map((experience, index) => (
          <TimelineCard
            key={experience.id}
            data={experience}
            index={index}
            isLeft={index % 2 === 0}
            icon={<Briefcase className="w-5 h-5" />}
          />
        ))}
      </div>
    </div>
  );
}

function EducationTimeline() {
  const education = [
    {
      id: 1,
      degree: "BSc in Computer Science",
      school: "University of British Columbia",
      logo: "/ubc.png",
      period: "2023 - 2027",
      description:
        "<b>GPA:</b> 89% <br/> <b>Awards:</b> Trek Academic Excellence Scholarship <br/> <b>Extracurriculars:</b> UBC Launch Pad Design Team, Intramural Basketball, Weight Lifting and Entrepreneurship Club",
      courses: [
        "Data Structures",
        "Web Development",
        "Software Engineering",
        "Discrete Mathematics",
        "Computer Systems",
        "Machine Learning",
      ],
      link: "https://www.ubc.ca/",
    },
    {
      id: 2,
      degree: "Certications",
      school: "Udemy",
      logo: "/udemy.png",
      period: "May 2024 - Aug 2024",
      description:
        "Completed two Udemy courses. One on Full Stack Web Development, learning how to build responsive websites and web applications. And another on Machine Learning, diving into the world of AI and data science using extensive frameworks and libraries in Python and R.",
      courses: [
        "The Complete 2024 Web Development Bootcamp",
        "Machine Learning A-Z",
      ],
      link: "#",
    },
  ];

  return (
    <div className="relative w-full">
      {/* Timeline line */}
      <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-0.5 bg-gradient-to-b from-emerald-500 to-teal-600" />

      {/* Education cards */}
      <div className="relative">
        {education.map((edu, index) => (
          <TimelineCard
            key={edu.id}
            data={{
              title: edu.school,
              company: edu.degree,
              logo: edu.logo,
              period: edu.period,
              description: edu.description,
              courses: edu.courses,
              link: edu.link,
            }}
            index={index}
            isLeft={index % 2 === 0}
            icon={<GraduationCap className="w-5 h-5" />}
          />
        ))}
      </div>
    </div>
  );
}

function TimelineCard({ data, index, isLeft, icon, type }) {
  const cardRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: cardRef,
    offset: ["start end", "end start"],
  });

  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.9, 1], [0, 1, 1, 0]);
  const x = useTransform(
    scrollYProgress,
    [0, 0.2, 0.9, 1],
    [isLeft ? -50 : 50, 0, 0, isLeft ? -50 : 50],
  );

  return (
    <div
      ref={cardRef}
      className={`relative mb-24 flex gap-x-[5rem] items-end md:items-center`}
    >
      {/* Timeline dot */}
      <div className="absolute left-1/2 transform -translate-x-1/2 z-10">
        <motion.div
          className={`w-12 h-12 rounded-full flex items-center justify-center border-4 ${
            isLeft
              ? "bg-blue-900 border-blue-400"
              : "bg-purple-900 border-purple-400"
          }`}
          initial={{ scale: 0 }}
          whileInView={{ scale: 1 }}
          viewport={{ once: true }}
          transition={{
            type: "spring",
            stiffness: 260,
            damping: 20,
            delay: 0.1 + index * 0.1,
          }}
        >
          {icon}
        </motion.div>
      </div>

      {/* Card */}
      <motion.div
        className={`relative w-full md:w-5/12 ${isLeft ? "md:mr-auto" : "md:ml-auto"} `}
        style={{ opacity, x }}
        whileHover={{ y: -5 }}
        transition={{ type: "spring", stiffness: 300, damping: 15 }}
      >
        <div className="bg-slate-800 rounded-xl shadow-xl overflow-hidden transform transition-all duration-300 hover:shadow-2xl">
          <div className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-xl font-bold text-white">{data.title}</h3>
                <div className="flex items-center mt-1">
                  <span className="text-slate-300 font-medium">
                    {data.company}
                  </span>
                </div>
              </div>
              <div className="flex-shrink-0 ml-4">
                <div className="w-16 h-16 rounded-md overflow-hidden bg-slate-700 p-2 flex items-center justify-center">
                  <img
                    src={data.logo || "/placeholder.svg"}
                    alt={`${data.company} logo`}
                    className="max-w-full max-h-full"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center text-sm text-slate-400 mb-4">
              <Calendar className="w-4 h-4 mr-1" />
              <span>{data.period}</span>
            </div>

            <p
              className="text-slate-300 mb-4"
              dangerouslySetInnerHTML={{ __html: data.description }}
            ></p>

            <div className="mb-4">
              <h4 className="text-sm font-semibold text-slate-200 mb-2">
                {data.skills ? "Skills & Technologies" : "Courses"}
              </h4>
              <div className="flex flex-wrap gap-2">
                {data.skills
                  ? data.skills.map((skill, i) => (
                      <span
                        key={i}
                        className={`text-xs px-2.5 py-1 rounded-full ${
                          isLeft
                            ? "bg-blue-900 text-blue-200"
                            : "bg-purple-900 text-purple-200"
                        }`}
                      >
                        {skill}
                      </span>
                    ))
                  : data.courses.map((skill, i) => (
                      <span
                        key={i}
                        className={`text-xs px-2.5 py-1 rounded-full ${
                          isLeft
                            ? "bg-blue-900 text-blue-200"
                            : "bg-purple-900 text-purple-200"
                        }`}
                      >
                        {skill}
                      </span>
                    ))}
              </div>
            </div>

            <a
              href={data.link}
              className={`inline-flex items-center text-sm font-medium ${
                isLeft
                  ? "text-blue-400 hover:text-blue-300"
                  : "text-purple-400 hover:text-purple-300"
              }`}
            >
              Learn more <ExternalLink className="w-3.5 h-3.5 ml-1" />
            </a>
          </div>

          <div
            className={`h-1.5 ${
              isLeft
                ? "bg-gradient-to-r from-blue-600 to-blue-400"
                : "bg-gradient-to-r from-purple-600 to-purple-400"
            }`}
          />
        </div>
      </motion.div>
    </div>
  );
}
